import React, { useState } from 'react';
import { ArrowLeft, Mail, Send, Check, X, AlertCircle, Copy } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { customerApi } from '../services/customerApi';
import logo from '../assets/logo.gif';

interface FormData {
  subject: string;
  message: string;
}

interface SubmissionResult {
  success: boolean;
  ticketId?: string;
  message?: string;
  error?: string;
}

const CustomerSupport: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [submissionResult, setSubmissionResult] = useState<SubmissionResult | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showCopiedFeedback, setShowCopiedFeedback] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const copyTicketId = async (ticketId: string) => {
    try {
      await navigator.clipboard.writeText(ticketId);
      setShowCopiedFeedback(true);
      setTimeout(() => setShowCopiedFeedback(false), 2000);
    } catch (err) {
      // If clipboard API fails, show the ticket ID in an alert
      alert(`Please manually copy this ticket ID: ${ticketId}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.message.trim()) {
      return;
    }

    setStatus('submitting');
    setSubmissionResult(null);

    try {
      // Use real API call
      const response = await customerApi.sendSupportTicket({
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        userEmail: user?.email || '',
        userName: user?.displayName || user?.email?.split('@')[0] || 'User'
      });

      setSubmissionResult({
        success: true,
        ticketId: response.data.ticketId,
        message: response.message
      });
      setStatus('success');
      setShowSuccessModal(true);
      
      // Clear form
      setFormData({ subject: '', message: '' });
    } catch (error) {
      setSubmissionResult({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to submit ticket. Please try again.'
      });
      setStatus('error');
    }
  };

  const handleRetry = () => {
    setStatus('idle');
    setSubmissionResult(null);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleReturnToDashboard = () => {
    setShowSuccessModal(false);
    navigate('/');
  };

  const isFormValid = formData.subject.trim() && formData.message.trim();
  const isSubmitting = status === 'submitting';

  const SuccessModal = () => (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#161513] rounded-none shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6 border border-[#0CB4E4]/30">
        <div className="text-center">
          <div className="w-16 h-16 bg-[#0CB4E4]/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-[#0CB4E4]/30">
            <Check className="w-8 h-8 text-[#0CB4E4]" />
          </div>
          
          <h3 className="text-xl font-semibold text-white mb-2">
            Ticket Submitted Successfully!
          </h3>
          
          <p className="text-gray-300 mb-4">
            Your support request has been received and assigned ticket ID:
          </p>
          
          <div className="bg-[#161513] rounded-lg p-4 mb-4 border border-[#0CB4E4]/30">
            <div className="flex items-center justify-between">
              <span className="font-mono text-lg font-semibold text-[#0CB4E4]">
                {submissionResult?.ticketId}
              </span>
              <div className="relative">
                <button
                  onClick={() => copyTicketId(submissionResult?.ticketId || '')}
                  className="p-2 text-gray-300 hover:text-white hover:bg-[#0CB4E4]/20 rounded-lg transition-colors"
                  title="Copy ticket ID"
                >
                  <Copy className="w-4 h-4" />
                </button>
                {showCopiedFeedback && (
                  <div className="absolute -top-10 -right-2 bg-[#0C5484] text-white text-xs px-2 py-1 rounded-md shadow-lg">
                    Copied!
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-transparent border-t-[#0CB4E4]"></div>
                  </div>
                )}
               </div>
            </div>
          </div>
          
          <div className="bg-[#0CB4E4]/10 rounded-none p-4 mb-6 border border-[#0CB4E4]/30">
            <h4 className="font-medium text-white mb-2">What happens next:</h4>
            <ul className="text-sm text-gray-300 text-left space-y-1">
              <li>• Our support team will review your request</li>
              <li>• We typically respond within 24 hours</li>
              <li>• You'll receive updates via email</li>
              <li>• Keep your ticket ID for reference</li>
            </ul>
          </div>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={handleReturnToDashboard}
            className="flex-1 px-4 py-2 bg-[#0CB4E4] text-[#161513] rounded-xl hover:bg-[#0CB4E4]/90 transition-colors font-medium shadow-md"
          >
            Return to Dashboard
          </button>
          <button
            onClick={() => setShowSuccessModal(false)}
            className="px-4 py-2 border border-[#0CB4E4]/30 text-white rounded-xl hover:bg-[#161513] transition-colors"
          >
            Submit Another
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#161513] py-8 px-4 relative">
      {/* Logo Background */}
      <div 
        className="absolute top-0 left-0 right-0 h-[400px] bg-cover bg-center bg-no-repeat opacity-20 z-0 pointer-events-none"
        style={{ backgroundImage: `url(${logo})` }}
      ></div>
    
      <div className="max-w-2xl mx-auto relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-bold text-3xl text-white">Customer Support</h1>
          
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
        </div>

        {/* Error Banner */}
        {status === 'error' && submissionResult?.error && (
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-400 mb-1">
                  Unable to Submit Ticket
                </h3>
                <p className="text-sm text-red-300 mb-3">
                  {submissionResult.error}
                </p>
                <button
                  onClick={handleRetry}
                  className="text-sm bg-red-900/30 text-red-300 px-3 py-1 rounded-md hover:bg-red-900/50 transition-colors border border-red-500/30"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Support Form */}
        <div className="bg-[#161513] rounded-none shadow-lg border border-[#0CB4E4]/30 p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-white mb-2">How can we help you?</h2>
            <p className="text-sm text-gray-300">
              Submit a support ticket and we'll get back to you within 24 hours. 
              You'll receive a unique ticket ID for tracking your request.
            </p>
          </div>

          <div className="space-y-6">
            {/* User Email Display */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                From
              </label>
              <div className="px-4 py-3 bg-[#161513] border border-[#0CB4E4]/30 rounded-none text-white">
                {user?.email || 'Not logged in'}
              </div>
            </div>

            {/* Subject Field */}
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-white mb-2">
                Subject <span className="text-red-400">*</span>
              </label>
             <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                disabled={isSubmitting}
                placeholder="Briefly describe your issue or question"
                className="w-full px-4 py-3 border rounded-none bg-[#161513] border-[#0CB4E4]/30 focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] transition-colors disabled:bg-[#161513]/50 disabled:text-gray-400 text-white"
                maxLength={200}
              />
              <div className="flex justify-between items-center mt-1">
                <div className="text-xs text-gray-400">
                  {formData.subject.length}/200 characters
                </div>
                {formData.subject.length > 180 && (
                  <div className="text-xs text-[#0CB4E4]">
                    {200 - formData.subject.length} characters remaining
                  </div>
                )}
              </div>
            </div>

            {/* Message Field */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
                Message <span className="text-red-400">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                disabled={isSubmitting}
                placeholder="Please provide a detailed description of your issue, question, or feedback. Include any relevant information that might help us assist you better."
                rows={8}
                className="w-full px-4 py-3 border rounded-none bg-[#161513] border-[#0CB4E4]/30 focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] transition-colors disabled:bg-[#161513]/50 disabled:text-gray-400 text-white resize-vertical"
                maxLength={2000}
              />
              <div className="flex justify-between items-center mt-1">
                <div className="text-xs text-gray-400">
                  {formData.message.length}/2000 characters
                </div>
                {formData.message.length > 1800 && (
                  <div className="text-xs text-[#0CB4E4]">
                    {2000 - formData.message.length} characters remaining
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-400">
                  <span className="text-red-400">*</span> Required fields
                </p>
              </div>

              <button
                onClick={handleSubmit}
                disabled={!isFormValid || isSubmitting}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#0CB4E4] to-[#0CB4E4]/80 text-[#161513] font-medium rounded-xl hover:from-[#0CB4E4]/90 hover:to-[#0CB4E4]/70 focus:ring-2 focus:ring-[#0CB4E4] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-[#161513] border-t-transparent rounded-full animate-spin" />
                    Submitting Ticket...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Submit Support Ticket
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Additional Support Info */}
          <div className="mt-8 pt-6 border-t border-[#0CB4E4]/20">
            <h3 className="text-sm font-medium text-white mb-3">Support Information:</h3>
            <div className="space-y-2 text-sm text-gray-300">
              <p>• We typically respond within 24 hours during business days</p>
              <p>• For urgent issues, include "URGENT" in your subject line</p>
              <p>• You'll receive email updates on your ticket progress</p>
              <p>• Keep your ticket ID for easy reference</p>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && <SuccessModal />}
    </div>
  );
};

export default CustomerSupport;
